import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  Activity,
  Shield,
  Plus,
  Minus,
  Trash2,
  RefreshCw,
  User,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";

interface UserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  phone: string | null;
  created_at: string;
  last_seen_at: string | null;
}

interface UserCredit {
  current_balance: number;
  lifetime_earned: number;
  lifetime_spent: number;
  plan_id: string | null;
}

interface CreditTransaction {
  id: string;
  action_type: string;
  credits_change: number;
  credits_before: number;
  credits_after: number;
  description: string | null;
  created_at: string;
}

interface Plan {
  id: string;
  name: string;
  monthly_credits: number;
}

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [credits, setCredits] = useState<UserCredit | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [showDeductCredits, setShowDeductCredits] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);

  // Form state
  const [creditsAmount, setCreditsAmount] = useState(0);
  const [creditsReason, setCreditsReason] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchUserData = async () => {
    if (!userId) return;
    setLoading(true);

    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (profileError) throw profileError;
      setProfile(profileData);

      // Fetch credits
      const { data: creditsData } = await supabase
        .from("user_credits")
        .select("*")
        .eq("user_id", userId)
        .single();

      setCredits(creditsData);

      // Fetch transactions
      const { data: txData } = await supabase
        .from("credit_transactions")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);

      setTransactions(txData || []);

      // Fetch plans
      const { data: plansData } = await supabase
        .from("subscription_plans")
        .select("id, name, monthly_credits")
        .order("sort_order");

      setPlans(plansData || []);

      // Check admin status
      const { data: rolesData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      setIsAdmin(rolesData?.some((r) => r.role === "admin") || false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast({ title: "Error loading user", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const handleAddCredits = async () => {
    if (!userId || creditsAmount <= 0) return;
    setActionLoading(true);

    try {
      const { error } = await supabase.functions.invoke("credit-service", {
        body: {
          action: "add_credits",
          target_user_id: userId,
          amount: creditsAmount,
          description: creditsReason || "Admin adjustment",
        },
      });

      if (error) throw error;

      toast({ title: "Credits added successfully" });
      setShowAddCredits(false);
      setCreditsAmount(0);
      setCreditsReason("");
      fetchUserData();
    } catch (error: any) {
      toast({ title: "Error adding credits", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeductCredits = async () => {
    if (!userId || !credits || creditsAmount <= 0) return;
    setActionLoading(true);

    try {
      const actualDeduction = Math.min(creditsAmount, credits.current_balance);
      const newBalance = credits.current_balance - actualDeduction;

      const { error: updateError } = await supabase
        .from("user_credits")
        .update({
          current_balance: newBalance,
          lifetime_spent: credits.lifetime_spent + actualDeduction,
        })
        .eq("user_id", userId);

      if (updateError) throw updateError;

      await supabase.from("credit_transactions").insert({
        user_id: userId,
        action_type: "admin_deduction",
        credits_change: -actualDeduction,
        credits_before: credits.current_balance,
        credits_after: newBalance,
        description: creditsReason || "Admin deduction",
      });

      toast({ title: "Credits deducted successfully" });
      setShowDeductCredits(false);
      setCreditsAmount(0);
      setCreditsReason("");
      fetchUserData();
    } catch (error: any) {
      toast({ title: "Error deducting credits", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!userId || !selectedPlanId) return;
    setActionLoading(true);

    try {
      const { error } = await supabase
        .from("user_credits")
        .update({ plan_id: selectedPlanId })
        .eq("user_id", userId);

      if (error) throw error;

      const plan = plans.find((p) => p.id === selectedPlanId);
      toast({ title: "Plan updated", description: `Changed to ${plan?.name}` });
      setShowChangePlan(false);
      setSelectedPlanId("");
      fetchUserData();
    } catch (error: any) {
      toast({ title: "Error changing plan", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId) return;
    setActionLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { userId },
      });

      if (error || !data?.success) {
        throw error || new Error(data?.error || "Failed to delete user");
      }

      toast({ title: "User deleted" });
      navigate("/admin/users");
    } catch (error: any) {
      toast({ title: "Error deleting user", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCurrentPlanName = () => {
    if (!credits?.plan_id) return "No Plan";
    const plan = plans.find((p) => p.id === credits.plan_id);
    return plan?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">User not found</p>
        <Link to="/admin/users">
          <Button variant="outline" className="mt-4">
            Back to Users
          </Button>
        </Link>
      </div>
    );
  }

  const displayName = profile.display_name || `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Unknown";

  const derivedPassword = (() => {
    const lastName = profile.last_name?.toLowerCase() || "";
    const phoneDigits = profile.phone?.replace(/\D/g, "") || "";
    const last4 = phoneDigits.slice(-4);
    if (!lastName || !last4) return null;
    return lastName + last4;
  })();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied to clipboard` });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/users")}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                {displayName}
                {isAdmin && (
                  <Badge variant="secondary">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                )}
              </h1>
              <p className="text-muted-foreground">{profile.email}</p>
            </div>
          </div>
        </div>
        <Button variant="destructive" onClick={() => setShowDeleteUser(true)}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete User
        </Button>
      </div>

      {/* Login Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Login Credentials
          </CardTitle>
          <CardDescription>Auto-generated credentials from Quick Onboard</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs text-muted-foreground">Username (Email)</Label>
              <p className="text-sm font-medium">{profile.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => copyToClipboard(profile.email, "Email")}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs text-muted-foreground">Password (derived)</Label>
              {derivedPassword ? (
                <p className="text-sm font-medium font-mono">
                  {showPassword ? derivedPassword : "••••••••"}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">Cannot derive — missing last name or phone</p>
              )}
            </div>
            {derivedPassword && (
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(derivedPassword, "Password")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
            ⚠️ This is the auto-generated password (lastname + last 4 phone digits). If the user changed their password, this will not be accurate.
          </p>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Contact Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{profile.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{profile.phone || "Not provided"}</span>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Joined {formatDate(profile.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Credits & Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Credits & Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Current Balance</span>
              <span className="text-2xl font-bold">{credits?.current_balance ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Lifetime Earned</span>
              <span className="font-medium text-green-600">{credits?.lifetime_earned ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Lifetime Spent</span>
              <span className="font-medium text-amber-600">{credits?.lifetime_spent ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Plan</span>
              <Badge variant="outline">{getCurrentPlanName()}</Badge>
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" className="flex-1" onClick={() => setShowAddCredits(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowDeductCredits(true)}>
                <Minus className="h-4 w-4 mr-1" />
                Deduct
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowChangePlan(true)}>
                Change Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Activity Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last Seen</span>
              <span className="text-sm">{formatDate(profile.last_seen_at)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Transactions</span>
              <span className="font-medium">{transactions.length}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <CardDescription>Recent credit transactions for this user</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Change</TableHead>
                <TableHead>Before</TableHead>
                <TableHead>After</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="text-muted-foreground">{formatDate(tx.created_at)}</TableCell>
                    <TableCell className="font-medium">
                      {tx.action_type.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                    </TableCell>
                    <TableCell>
                      <Badge variant={tx.credits_change > 0 ? "default" : "secondary"}>
                        {tx.credits_change > 0 ? "+" : ""}{tx.credits_change}
                      </Badge>
                    </TableCell>
                    <TableCell>{tx.credits_before}</TableCell>
                    <TableCell>{tx.credits_after}</TableCell>
                    <TableCell className="text-muted-foreground">{tx.description || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Credits Dialog */}
      <Dialog open={showAddCredits} onOpenChange={setShowAddCredits}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Credits</DialogTitle>
            <DialogDescription>Add credits to {displayName}'s account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                min={1}
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(Number(e.target.value))}
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                placeholder="e.g., Bonus credits, refund..."
                value={creditsReason}
                onChange={(e) => setCreditsReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCredits(false)}>Cancel</Button>
            <Button onClick={handleAddCredits} disabled={actionLoading || creditsAmount <= 0}>
              {actionLoading ? "Adding..." : "Add Credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deduct Credits Dialog */}
      <Dialog open={showDeductCredits} onOpenChange={setShowDeductCredits}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deduct Credits</DialogTitle>
            <DialogDescription>Remove credits from {displayName}'s account (current: {credits?.current_balance ?? 0})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                min={1}
                max={credits?.current_balance ?? 0}
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(Number(e.target.value))}
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                placeholder="e.g., Adjustment, correction..."
                value={creditsReason}
                onChange={(e) => setCreditsReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeductCredits(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeductCredits} disabled={actionLoading || creditsAmount <= 0}>
              {actionLoading ? "Deducting..." : "Deduct Credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlan} onOpenChange={setShowChangePlan}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Plan</DialogTitle>
            <DialogDescription>Change {displayName}'s subscription plan</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Plan</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} ({plan.monthly_credits} credits/month)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePlan(false)}>Cancel</Button>
            <Button onClick={handleChangePlan} disabled={actionLoading || !selectedPlanId}>
              {actionLoading ? "Updating..." : "Change Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteUser} onOpenChange={setShowDeleteUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete {displayName}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteUser(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
